import { config } from "../Environment";

export interface UserCategory {
  _id: string;
  fiUser: string;
  name: string;
  color: string;
  type: "action" | "insight" | "both";
  createdOn: number;
  updatedOn: number;
}

export interface CreateCategoryRequest {
  name: string;
  color?: string;
  type?: "action" | "insight" | "both";
}

export interface EditCategoryRequest {
  id: string;
  name?: string;
  color?: string;
  type?: "action" | "insight" | "both";
}

export interface DeleteCategoryRequest {
  id: string;
}

export interface CategoryResponse {
  status: "CREATED" | "UPDATED" | "DELETED" | "ERROR";
  _id?: string;
  category?: UserCategory;
  fault?: string;
}

export interface ListCategoriesResponse {
  status: "LIST";
  list: UserCategory[];
}

/**
 * Create a new category for the authenticated user
 */
export const createCategory = async (
  retryFetch: Function,
  data: CreateCategoryRequest
): Promise<CategoryResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-category/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create category: ${response.statusText}`);
  }

  const result: CategoryResponse = await response.json();
  return result;
};

/**
 * Edit an existing category
 */
export const editCategory = async (
  retryFetch: Function,
  data: EditCategoryRequest
): Promise<CategoryResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-category/edit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to edit category: ${response.statusText}`);
  }

  const result: CategoryResponse = await response.json();
  return result;
};

/**
 * Delete a category
 */
export const deleteCategory = async (
  retryFetch: Function,
  data: DeleteCategoryRequest
): Promise<CategoryResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-category/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete category: ${response.statusText}`);
  }

  const result: CategoryResponse = await response.json();
  return result;
};

/**
 * List all categories for the authenticated user
 */
export const listCategories = async (
  retryFetch: Function,
  type?: "action" | "insight" | "both"
): Promise<ListCategoriesResponse> => {
  const queryParams = new URLSearchParams();
  if (type) {
    queryParams.append("type", type);
  }

  const url = `${config.API_BASE}/api/user-category/list${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await retryFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.statusText}`);
  }

  const result: ListCategoriesResponse = await response.json();
  return result;
};
